import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../../../i18n'
import { useAuth } from '../../AuthProvider'
import { Button } from '../../ui/Button'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { featureFlags } from '../../../config/featureFlags'
import {
  bulkLinkQuestionEvidence,
  fetchQuestionEvidenceState,
  linkQuestionEvidence,
  linkQuestionPolicies,
  unlinkQuestionEvidence,
  type LinkedEvidenceRow,
  type QuestionEvidenceState,
} from '../../../services/questionEvidenceBindingApi'
import { LinkedEvidenceChips } from './LinkedEvidenceChips'
import { QuestionEvidenceUUIDInput } from './QuestionEvidenceUUIDInput'
import { QuestionEvidenceLibraryPicker, type LibraryEvidenceRow } from './QuestionEvidenceLibraryPicker'
import { QuestionGovernanceLibraryPicker } from './QuestionGovernanceLibraryPicker'
import { EvidenceSufficiencyIndicator } from './EvidenceSufficiencyIndicator'
import { toEvidenceDisplayId } from '../../../utils/evidenceDisplayId'
import { API_ENDPOINTS } from '../../../config/api'
import { fetchPlatformEvidence, linkPlatformEvidence, type PlatformEvidenceCard } from '../../../services/complianceLifecycleApi'
import { PlatformEvidencePicker } from './PlatformEvidencePicker'

interface Props {
  sessionId: string
  domainKey: string
  questionKey: string
  canonicalControlCode?: string
  standardCode?: string
  answerValue?: string
  /** Legacy governance rows for library section */
  govEvidence?: Array<{ id: string; title?: string; freshnessStatus?: string; status?: string; verificationStatus?: string }>
  onLinkedChange?: (evidenceIds: string[]) => void
  /** Enterprise control-centric shell — matches Compliance Center cards */
  variant?: 'default' | 'enterprise'
}

function evidenceIdsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export function QuestionEvidenceBindingPanel({
  sessionId,
  domainKey,
  questionKey,
  canonicalControlCode,
  standardCode,
  answerValue,
  govEvidence = [],
  onLinkedChange,
  variant = 'default',
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [state, setState] = useState<QuestionEvidenceState | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerTab, setPickerTab] = useState<'evidence' | 'policies'>('evidence')
  const [unlinking, setUnlinking] = useState(false)
  const [pickerLibrary, setPickerLibrary] = useState<LibraryEvidenceRow[]>([])
  const [pickerLibraryLoading, setPickerLibraryLoading] = useState(false)
  const [pickerLibraryError, setPickerLibraryError] = useState<string | null>(null)
  const [platformRecommended, setPlatformRecommended] = useState<PlatformEvidenceCard[]>([])
  const [platformOther, setPlatformOther] = useState<PlatformEvidenceCard[]>([])
  const [platformLoading, setPlatformLoading] = useState(false)
  const [platformPickerOpen, setPlatformPickerOpen] = useState(false)
  const [platformError, setPlatformError] = useState<string | null>(null)
  const [platformLinkingSource, setPlatformLinkingSource] = useState<string | null>(null)
  const platformPanelRef = useRef<HTMLDivElement | null>(null)

  const enabled = featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1
  const onLinkedChangeRef = useRef(onLinkedChange)
  const lastNotifiedIdsRef = useRef<string[]>([])
  const skipNextNotifyRef = useRef(variant === 'enterprise')

  useEffect(() => {
    onLinkedChangeRef.current = onLinkedChange
  }, [onLinkedChange])

  const notifyLinkedIds = useCallback((ids: string[]) => {
    if (evidenceIdsEqual(lastNotifiedIdsRef.current, ids)) return
    lastNotifiedIdsRef.current = ids
    if (skipNextNotifyRef.current) {
      skipNextNotifyRef.current = false
      return
    }
    onLinkedChangeRef.current?.(ids)
  }, [])

  const refresh = useCallback(
    async (answerOverride?: string) => {
      if (!enabled || !token || !sessionId) return
      setLoading(true)
      setError(null)
      try {
        const data = await fetchQuestionEvidenceState(token, {
          sessionId,
          questionKey,
          domainKey,
          canonicalControlCode,
          standardCode,
          answerValue: answerOverride ?? answerValue,
        })
        setState(data)
        const ids = (data.linkedEvidence || []).map((e) => e.evidenceId)
        notifyLinkedIds(ids)
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load evidence binding')
      } finally {
        setLoading(false)
      }
    },
    [
      enabled,
      token,
      sessionId,
      questionKey,
      domainKey,
      canonicalControlCode,
      standardCode,
      answerValue,
      notifyLinkedIds,
    ],
  )

  const questionScopeKey = `${sessionId}::${domainKey}::${questionKey}::${canonicalControlCode || ''}::${standardCode || ''}`

  useEffect(() => {
    lastNotifiedIdsRef.current = []
    skipNextNotifyRef.current = variant === 'enterprise'
    setPlatformPickerOpen(false)
    setPlatformRecommended([])
    setPlatformOther([])
    setPlatformError(null)
    void refresh()
    // Load when question context changes only — not when parent passes new callback refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionScopeKey, token, enabled])

  const loadPlatformEvidence = useCallback(async () => {
    if (!token || !standardCode) return
    setPlatformPickerOpen(true)
    setPickerOpen(false)
    setPlatformLoading(true)
    setPlatformError(null)
    setPlatformRecommended([])
    setPlatformOther([])
    try {
      const data = await fetchPlatformEvidence(token, {
        standardCode,
        controlKey: canonicalControlCode || undefined,
        sessionId,
        questionId: questionKey,
      })
      setPlatformRecommended(Array.isArray(data?.recommended) ? data.recommended : [])
      setPlatformOther(Array.isArray(data?.other) ? data.other : [])
    } catch (e: unknown) {
      setPlatformRecommended([])
      setPlatformOther([])
      setPlatformError(
        e instanceof Error
          ? e.message
          : t('complianceEvidence.platformLoadFailed', 'Failed to load platform evidence'),
      )
    } finally {
      setPlatformLoading(false)
      window.requestAnimationFrame(() => {
        platformPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    }
  }, [token, standardCode, canonicalControlCode, sessionId, questionKey, t])

  useEffect(() => {
    if (answerValue == null || answerValue === '') return
    const timer = window.setTimeout(() => {
      void refresh(answerValue)
    }, 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerValue, questionScopeKey])

  const linked: LinkedEvidenceRow[] = state?.linkedEvidence || []
  const linkedIdSet = useMemo(
    () => new Set(linked.map((e) => e.evidenceId).filter((id): id is string => Boolean(id))),
    [linked],
  )
  const linkedPolicyIds = useMemo(
    () =>
      linked
        .filter((e) => e.policyId || e.source === 'POLICY_LIBRARY')
        .map((e) => String(e.policyId || ''))
        .filter(Boolean),
    [linked],
  )

  const suggested: LibraryEvidenceRow[] = (state?.suggestedEvidence || []).map((r) => ({
    id: r.id,
    displayId: r.displayId,
    title: r.title,
    verificationStatus: r.verificationStatus,
    freshnessStatus: r.freshnessStatus,
    mappedControls: r.mappedControls,
    mappedStandards: r.mappedStandards,
    crossStandardReuse: r.crossStandardReuse,
    suggestionReason: r.suggestionReason,
  }))

  const library: LibraryEvidenceRow[] = useMemo(() => {
    const byId = new Map<string, LibraryEvidenceRow>()
    const add = (row: LibraryEvidenceRow) => {
      if (!row.id) return
      const prev = byId.get(row.id)
      byId.set(row.id, { ...prev, ...row })
    }
    for (const r of pickerLibrary) add(r)
    for (const r of govEvidence) {
      add({
        id: r.id,
        displayId: toEvidenceDisplayId(r.id),
        title: r.title,
        freshnessStatus: r.freshnessStatus,
        verificationStatus: (r as { verificationStatus?: string }).verificationStatus,
      })
    }
    for (const r of suggested) add(r)
    return [...byId.values()]
  }, [pickerLibrary, govEvidence, suggested])

  useEffect(() => {
    if (!pickerOpen || pickerTab !== 'evidence' || !token) return
    let cancelled = false

    const mapRows = (rows: Record<string, unknown>[]): LibraryEvidenceRow[] =>
      rows.map((x) => ({
        id: String(x.id),
        displayId: toEvidenceDisplayId(String(x.id)),
        title: typeof x.title === 'string' ? x.title : undefined,
        verificationStatus:
          typeof x.verificationStatus === 'string' ? x.verificationStatus : undefined,
        freshnessStatus: typeof x.freshnessStatus === 'string' ? x.freshnessStatus : undefined,
      }))

    const fetchEvidence = async (controlCode?: string) => {
      const params = new URLSearchParams()
      if (controlCode) params.set('controlCode', controlCode)
      const qs = params.toString()
      const res = await fetch(
        `${API_ENDPOINTS.GOVERNANCE_EVIDENCE}${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.error || json?.message || `Evidence list ${res.status}`)
      return Array.isArray(json?.evidence) ? json.evidence : []
    }

    setPickerLibraryLoading(true)
    setPickerLibraryError(null)
    ;(async () => {
      try {
        let rows = await fetchEvidence(canonicalControlCode)
        if (!rows.length && canonicalControlCode) {
          rows = await fetchEvidence()
        }
        if (!cancelled) setPickerLibrary(mapRows(rows))
      } catch (e: unknown) {
        if (!cancelled) {
          setPickerLibrary([])
          setPickerLibraryError(e instanceof Error ? e.message : 'Failed to load evidence library')
        }
      } finally {
        if (!cancelled) setPickerLibraryLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [pickerOpen, pickerTab, token, canonicalControlCode])

  const handleLink = async (evidenceIds: string[], linkageSource: 'MANUAL' | 'AUTO_SUGGESTED' | 'AUTO_REUSED') => {
    if (!token) return
    await linkQuestionEvidence(token, {
      sessionId,
      questionKey,
      domainKey,
      evidenceIds,
      linkageSource,
    })
    await refresh()
  }

  if (!enabled) return null

  const shellClass =
    variant === 'enterprise'
      ? listStyles.evidenceBindingShell
      : `${listStyles.evidenceBindingShell} ${listStyles.panel}`

  return (
    <div className={shellClass}>
      <div className={listStyles.evidenceBindingHeader}>
        <h4 className={listStyles.evidenceBindingTitle}>
          {variant === 'enterprise'
            ? t('complianceEvidence.linkedTitle', 'Linked evidence')
            : t('complianceEvidence.panelTitle', 'Evidence binding')}
        </h4>
        <EvidenceSufficiencyIndicator state={state?.sufficiency?.state} reason={state?.sufficiency?.reason} />
      </div>

      <div className={`${listStyles.evidenceBindingActions} flex flex-wrap gap-2`}>
        <Button
          type="button"
          size="small"
          color="primary"
          disabled={platformLoading || !standardCode}
          onClick={() => {
            void loadPlatformEvidence()
          }}
          data-testid="btn-link-platform-controls"
          title={
            !canonicalControlCode
              ? t(
                  'complianceEvidence.platformNeedsControl',
                  'Opens platform controls for this standard (control code optional)',
                )
              : undefined
          }
        >
          {platformLoading
            ? t('common.loading', 'Loading…')
            : t('complianceEvidence.linkPlatformControls', 'Link platform controls')}
        </Button>
        <Button
          type="button"
          size="small"
          color="primary"
          onClick={() => {
            setPlatformPickerOpen(false)
            setPickerTab('evidence')
            setPickerOpen(true)
          }}
          data-testid="btn-link-evidence"
        >
          {t('complianceEvidence.linkEvidence', 'Link evidence')}
        </Button>
        {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => {
              setPickerTab('policies')
              setPickerOpen(true)
            }}
          >
            {t('governance.documentLifecycle.selectPolicies', 'Select from Policies & Governance')}
          </Button>
        ) : null}
      </div>

      {platformPickerOpen ? (
        <div ref={platformPanelRef}>
          <PlatformEvidencePicker
            recommended={platformRecommended}
            other={platformOther}
            loading={platformLoading}
            error={platformError}
            linkingSource={platformLinkingSource}
            onClose={() => {
              setPlatformPickerOpen(false)
              setPlatformRecommended([])
              setPlatformOther([])
              setPlatformError(null)
            }}
            onSaveSelected={async (sourceTypes) => {
              if (!token || !standardCode || !canonicalControlCode) return
              setPlatformLinkingSource('batch')
              setPlatformError(null)
              try {
                for (const sourceType of sourceTypes) {
                  await linkPlatformEvidence(token, {
                    standardCode,
                    controlKey: canonicalControlCode,
                    platformSource: sourceType,
                    sessionId,
                    questionId: questionKey,
                    responseId: state?.questionAnswerId,
                  })
                }
                setPlatformPickerOpen(false)
                setPlatformRecommended([])
                setPlatformOther([])
                await refresh()
              } catch (e: unknown) {
                setPlatformError(
                  e instanceof Error
                    ? e.message
                    : t('complianceEvidence.platformLinkFailed', 'Failed to link platform controls'),
                )
              } finally {
                setPlatformLinkingSource(null)
              }
            }}
          />
        </div>
      ) : null}

      {error ? <p className={`${listStyles.formError} mt-2`}>{error}</p> : null}
      {loading ? <p className={`${listStyles.pickerRowMeta} mt-2`}>{t('common.loading', 'Loading…')}</p> : null}

      <div className={listStyles.evidenceBindingSection}>
        {variant !== 'enterprise' ? (
          <p className={listStyles.pickerSectionTitle}>{t('complianceEvidence.linkedTitle', 'Linked evidence')}</p>
        ) : null}
        <LinkedEvidenceChips
          items={linked}
          unlinking={unlinking}
          onUnlink={async (item) => {
            if (!token || !item.evidenceId) return
            setUnlinking(true)
            try {
              await unlinkQuestionEvidence(token, { sessionId, questionKey, evidenceId: item.evidenceId })
              await refresh()
            } finally {
              setUnlinking(false)
            }
          }}
        />
      </div>

      <div className={listStyles.evidenceBindingSection}>
        <QuestionEvidenceUUIDInput
          disabled={loading}
          onSubmit={async (rawInput) => {
            if (!token) return
            const result = await bulkLinkQuestionEvidence(token, {
              sessionId,
              questionKey,
              domainKey,
              rawInput,
            })
            if (result.unresolved?.length) {
              setError(
                t('complianceEvidence.unresolved', 'Some IDs could not be resolved: {{n}}', {
                  n: result.unresolved.length,
                }),
              )
            }
            await refresh()
          }}
        />
      </div>

      {suggested.length && !linked.length ? (
        <section className={`${listStyles.evidenceBindingSection} ${listStyles.pickerBannerSuccess}`}>
          <p className={listStyles.pickerSectionTitle}>{t('complianceEvidence.suggested', 'Suggested evidence')}</p>
          <ul className={`${listStyles.pickerList} mt-2`}>
            {suggested.slice(0, 5).map((s) => (
              <li key={s.id} className={listStyles.pickerRow}>
                <span className={`${listStyles.pickerRowTitle} min-w-0 flex-1`}>{s.title}</span>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  className="shrink-0"
                  onClick={() => void handleLink([s.id], 'AUTO_SUGGESTED')}
                >
                  {t('complianceEvidence.linkOne', 'Link')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {pickerOpen && pickerTab === 'evidence' ? (
        <QuestionEvidenceLibraryPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          suggested={suggested}
          library={library}
          linkedIds={linkedIdSet}
          reuseCandidates={state?.reuseCandidates}
          loading={pickerLibraryLoading}
          loadError={pickerLibraryError}
          onConfirmLink={handleLink}
        />
      ) : null}
      {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
        <QuestionGovernanceLibraryPicker
          open={pickerOpen && pickerTab === 'policies'}
          onClose={() => setPickerOpen(false)}
          selectedIds={linkedPolicyIds}
          onSelect={async (policyId, policyVersionId) => {
            if (!token) return
            await linkQuestionPolicies(token, {
              sessionId,
              questionKey,
              domainKey,
              policyRefs: [
                {
                  policyId,
                  policyVersionId,
                  referenceType: policyVersionId ? 'POLICY_DOCUMENT_VERSION' : 'POLICY_DOCUMENT',
                },
              ],
            })
            setPickerOpen(false)
            await refresh()
          }}
        />
      ) : null}
    </div>
  )
}
